/**
 * EvaluationsSearchPage — Tier 1 UI seed
 *
 * Minimal search interface for GET /api/evaluations/search.
 * Route: /evaluations/search
 *
 * Design choices:
 * - Plain <input type="date"> for date range — no extra date library.
 * - Filter form at top, results table below.
 * - Paginated with prev/next controls.
 * - "Showing X–Y of N" count line.
 * - Click row → navigate to /evaluations/{evaluation_id}.
 * - athlete dropdown: coach/admin only (hidden for athletes).
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTemplates } from '@/hooks/useTemplates';
import { useMyChannels } from '@/hooks/useChannels';
import { useCoachAthletes } from '@/hooks/useChannels';
import { useQuery } from '@tanstack/react-query';
import { searchEvaluations } from '@/services/evaluationSearchApi';
import type { EvaluationSearchParams } from '@/services/evaluationSearchApi';
import { formatDate } from '@/utils/formatters';
import { Skeleton } from '@/components/ui';

// ─── Sort options ─────────────────────────────────────────────────────────────

type SortBy = 'created_at' | 'overall_score' | 'form_score' | 'timing_score';
type Order = 'asc' | 'desc';

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: 'created_at', label: 'Date' },
  { value: 'overall_score', label: 'Overall Score' },
  { value: 'form_score', label: 'Form Score' },
  { value: 'timing_score', label: 'Timing Score' },
];

const PAGE_SIZE = 20;

// ─── Score badge ──────────────────────────────────────────────────────────────

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) {
    return <span style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>—</span>;
  }
  const color =
    score >= 80
      ? 'var(--color-success)'
      : score >= 60
        ? 'var(--color-warning)'
        : 'var(--color-error)';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '13px',
        fontWeight: 600,
        backgroundColor: `${color}22`,
        color,
      }}
    >
      {Math.round(score)}
    </span>
  );
}

// ─── Video status badge ───────────────────────────────────────────────────────

function VideoStatusBadge({ status }: { status: 'available' | 'expired' }) {
  if (status === 'expired') {
    return (
      <span
        title="Video file expired. Score and coaching feedback are still available."
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '999px',
          fontSize: '11px',
          fontWeight: 500,
          backgroundColor: 'var(--color-text-muted)22',
          color: 'var(--color-text-muted)',
        }}
      >
        Expired
      </span>
    );
  }
  return null;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EvaluationsSearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';

  // ── Filter state ────────────────────────────────────────────────────────────
  const [templateId, setTemplateId] = useState('');
  const [athleteId, setAthleteId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [order, setOrder] = useState<Order>('desc');
  const [offset, setOffset] = useState(0);

  // ── Data for dropdowns ──────────────────────────────────────────────────────
  const { data: templates } = useTemplates();
  const { data: channels } = useMyChannels();

  // Athlete dropdown: collect all athletes across all coach's channels.
  const channelSlugs = channels?.map((ch) => ch.slug) ?? [];
  const { data: athletes } = useCoachAthletes(
    isCoachOrAdmin ? channelSlugs : [],
    user?.id ? [user.id] : []
  );

  // ── Build search params ─────────────────────────────────────────────────────
  const buildParams = useCallback((): EvaluationSearchParams => {
    const params: EvaluationSearchParams = {
      limit: PAGE_SIZE,
      offset,
      sort_by: sortBy,
      order,
    };
    if (templateId) params.template_id = templateId;
    if (athleteId && isCoachOrAdmin) params.athlete_id = athleteId;
    if (channelId) params.channel_id = channelId;
    if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
    if (dateTo) params.date_to = new Date(dateTo).toISOString();
    return params;
  }, [templateId, athleteId, channelId, dateFrom, dateTo, sortBy, order, offset, isCoachOrAdmin]);

  // ── Query ───────────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useQuery({
    queryKey: ['evaluation-search', templateId, athleteId, channelId, dateFrom, dateTo, sortBy, order, offset],
    queryFn: () => searchEvaluations(buildParams()),
    enabled: !!user?.id,
  });

  // ── Pager helpers ───────────────────────────────────────────────────────────
  const total = data?.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromRow = total === 0 ? 0 : offset + 1;
  const toRow = Math.min(offset + PAGE_SIZE, total);

  function handleFilterChange(setter: (v: string) => void) {
    return (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      setter(e.target.value);
      setOffset(0); // Reset to first page on any filter change
    };
  }

  // ── Styles ──────────────────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: '8px',
    border: '1px solid var(--color-border-subtle)',
    backgroundColor: 'var(--color-dark-elevated-1)',
    color: 'var(--color-text-primary)',
    fontSize: '13px',
    minWidth: '160px',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--color-text-muted)',
    marginBottom: '4px',
    display: 'block',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* ── Page header ── */}
      <h1
        style={{
          fontSize: '22px',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: '20px',
        }}
      >
        Search Evaluations
      </h1>

      {/* ── Filter form ── */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'flex-end',
          }}
        >
          {/* Template */}
          <div>
            <label style={labelStyle}>Template</label>
            <select
              value={templateId}
              onChange={handleFilterChange(setTemplateId)}
              style={inputStyle}
              aria-label="Filter by template"
            >
              <option value="">All templates</option>
              {templates?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* Athlete (coach/admin only) */}
          {isCoachOrAdmin && (
            <div>
              <label style={labelStyle}>Athlete</label>
              <select
                value={athleteId}
                onChange={handleFilterChange(setAthleteId)}
                style={inputStyle}
                aria-label="Filter by athlete"
              >
                <option value="">All athletes</option>
                {athletes?.map((a) => (
                  <option key={a.user_id} value={a.user_id}>
                    {a.display_name || a.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Channel */}
          <div>
            <label style={labelStyle}>Channel</label>
            <select
              value={channelId}
              onChange={handleFilterChange(setChannelId)}
              style={inputStyle}
              aria-label="Filter by channel"
            >
              <option value="">All channels</option>
              {channels?.map((ch) => (
                <option key={String(ch.id)} value={String(ch.id)}>
                  {ch.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Date from — plain HTML date input (no extra library required) */}
          <div>
            <label style={labelStyle}>From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={handleFilterChange(setDateFrom)}
              style={inputStyle}
              aria-label="Filter from date"
            />
          </div>

          {/* Date to */}
          <div>
            <label style={labelStyle}>To</label>
            <input
              type="date"
              value={dateTo}
              onChange={handleFilterChange(setDateTo)}
              style={inputStyle}
              aria-label="Filter to date"
            />
          </div>

          {/* Sort by */}
          <div>
            <label style={labelStyle}>Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortBy);
                setOffset(0);
              }}
              style={inputStyle}
              aria-label="Sort by"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Order */}
          <div>
            <label style={labelStyle}>Order</label>
            <select
              value={order}
              onChange={(e) => {
                setOrder(e.target.value as Order);
                setOffset(0);
              }}
              style={inputStyle}
              aria-label="Sort order"
            >
              <option value="desc">Newest / Highest first</option>
              <option value="asc">Oldest / Lowest first</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Results count ── */}
      <div
        style={{
          fontSize: '13px',
          color: 'var(--color-text-muted)',
          marginBottom: '12px',
          minHeight: '20px',
        }}
      >
        {isLoading
          ? 'Loading…'
          : isError
            ? 'Error loading results.'
            : total === 0
              ? 'No evaluations found.'
              : `Showing ${fromRow}–${toRow} of ${total}`}
      </div>

      {/* ── Skeleton rows — shown while the first fetch is in progress ── */}
      {isLoading && (
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
          aria-busy="true"
          aria-label="Loading evaluations"
        >
          {/* Faux header matching the real table header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1.5fr 1.2fr 0.8fr 0.8fr 0.8fr 0.8fr',
              padding: '10px 16px',
              borderBottom: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-1)',
              gap: '16px',
            }}
          >
            {['Template', 'Athlete', 'Date', 'Overall', 'Form', 'Timing', 'Status'].map((col) => (
              <span
                key={col}
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.05em',
                  color: 'var(--color-text-muted)',
                }}
              >
                {col}
              </span>
            ))}
          </div>
          {/* 5 skeleton rows — column widths match the results table */}
          {([
            ['w-3/5', 'w-7/12', 'w-4/5'],
            ['w-3/4', 'w-2/3', 'w-3/4'],
            ['w-1/2', 'w-3/5', 'w-4/5'],
            ['w-4/5', 'w-1/2', 'w-3/5'],
            ['w-2/3', 'w-7/12', 'w-3/4'],
          ] as const).map((widths, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1.2fr 0.8fr 0.8fr 0.8fr 0.8fr',
                padding: '14px 16px',
                borderBottom: i < 4 ? '1px solid var(--color-border-subtle)' : undefined,
                gap: '16px',
                alignItems: 'center',
              }}
            >
              <Skeleton className={`h-4 rounded ${widths[0]}`} />
              <Skeleton className={`h-3 rounded ${widths[1]}`} />
              <Skeleton className={`h-3 rounded ${widths[2]}`} />
              <Skeleton className="h-6 w-12 rounded-full" />
              <Skeleton className="h-6 w-12 rounded-full" />
              <Skeleton className="h-6 w-12 rounded-full" />
              <Skeleton className="h-4 w-16 rounded-full" />
            </div>
          ))}
        </div>
      )}

      {/* ── Results table ── */}
      {!isLoading && !isError && data && data.items.length > 0 && (
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: '1px solid var(--color-border-subtle)',
                  backgroundColor: 'var(--color-dark-elevated-1)',
                }}
              >
                {(['Template', 'Athlete', 'Date', 'Overall', 'Form', 'Timing', 'Status'] as const).map(
                  (col) => (
                    <th
                      key={col}
                      style={{
                        padding: '10px 16px',
                        textAlign: 'left',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      {col}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr
                  key={item.evaluation_id}
                  onClick={() => navigate(`/evaluations/${item.evaluation_id}`)}
                  style={{
                    borderBottom: '1px solid var(--color-border-subtle)',
                    cursor: 'pointer',
                    transition: 'background-color 100ms ease',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                      'var(--color-dark-elevated-1)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                  }}
                >
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: 'var(--color-text-primary)',
                      fontWeight: 500,
                    }}
                  >
                    {item.template_name ?? '—'}
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {item.athlete_display_name ?? '—'}
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: '13px',
                      color: 'var(--color-text-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDate(item.created_at)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <ScoreBadge score={item.overall_score} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <ScoreBadge score={item.form_score} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <ScoreBadge score={item.timing_score} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <VideoStatusBadge status={item.video_status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pager ── */}
      {total > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '16px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-1)',
              color: offset === 0 ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
              cursor: offset === 0 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
            }}
          >
            Prev
          </button>
          <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!data?.has_more}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-1)',
              color: data?.has_more ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              cursor: data?.has_more ? 'pointer' : 'not-allowed',
              fontSize: '13px',
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
